import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Fade,
  IconButton,
  withStyles,
  useMediaQuery
} from '@material-ui/core';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import Button from 'components/Button';
import colors from 'utils/stylesheet/colors';
import { ReactComponent as CloseIcon } from 'assets/images/close.svg';
import { useTheme } from '@material-ui/core/styles';
import classNames from 'classnames';
import styles from './styles.module.scss';

const useStyles = (theme) => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(2.5),
    top: theme.spacing(2.5)
  },
  fullScreenCloseButton: {
    marginRight: theme.spacing(2.5),
    marginTop: theme.spacing(-0.5)
  }
});

const DialogTitle = withStyles(useStyles)((props) => {
  const { children, classes, onClose, fullScreen, ...other } = props;

  const closeButton = fullScreen ? classes.fullScreenCloseButton : classes.closeButton;
  return (
    <MuiDialogTitle disableTypography {...other}>
      {!fullScreen && children}
      {onClose ? (
        <IconButton className={closeButton} onClick={onClose} data-testid='close-button'>
          <CloseIcon />
        </IconButton>
      ) : null}
      {fullScreen && children}
    </MuiDialogTitle>
  );
});

const Modal = (props) => {
  const {
    open,
    handleClose,
    size,
    onCancel,
    title,
    content,
    cancelText,
    showActions,
    dangerAction,
    loading,
    error,
    confirmText,
    onConfirm,
    gameCenter,
    showTitle,
    showFullScreen,
    confirmation
  } = props;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Fade}
      maxWidth={size}
      scroll='paper'
      PaperProps={{
        square: true,
        style: {
          borderRadius: '0.5rem',
          background: colors.dark_blue,
          padding: showTitle ? '1rem' : '0'
        }
      }}
      fullScreen={typeof showFullScreen === 'boolean' ? showFullScreen : fullScreen}
      fullWidth
    >
      {showTitle && (
        <DialogTitle
          disableTypography
          className={classNames(styles.modalTitle, styles.commonBackground, {
            [styles.withoutLine]: gameCenter || confirmation
          })}
          onClose={onCancel}
          fullScreen={fullScreen}
        >
          {title}
        </DialogTitle>
      )}
      <DialogContent
        className={classNames(styles.modalContent, styles.commonBackground, {
          [styles.noPadding]: !showTitle
        })}
      >
        {content}
      </DialogContent>
      {showActions && (
        <DialogActions
          className={classNames(styles.modalActions, styles.commonBackground, {
            [styles.withoutLine]: gameCenter || confirmation
          })}
        >
          {!gameCenter && (
            <Button secondary onClick={onCancel}>
              {cancelText}
            </Button>
          )}
          <Button
            danger={dangerAction}
            disable={loading || error}
            onClick={onConfirm}
            gameCenter={gameCenter}
          >
            {confirmText}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

Modal.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  cancelText: PropTypes.string,
  confirmText: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  showActions: PropTypes.bool,
  dangerAction: PropTypes.bool,
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  handleClose: PropTypes.func.isRequired,
  gameCenter: PropTypes.bool,
  showTitle: PropTypes.bool,
  showFullScreen: PropTypes.oneOf([null, true, false]),
  confirmation: PropTypes.bool
};

Modal.defaultProps = {
  size: 'sm',
  showActions: true,
  showTitle: true,
  showFullScreen: null,
  cancelText: 'cancel',
  confirmText: 'Save',
  onConfirm: () => null,
  dangerAction: false,
  loading: false,
  error: false,
  gameCenter: false,
  confirmation: false
};

export default Modal;
